import { useCallback, useState } from "react";
import {
  UserProfileUpdateResponse,
  UserProfileUpdateResponseData,
  UserProfileUpdateFullRequest,
} from "../../types/user";
import { apiClient } from "../../utils/api";
import { useAuthStore } from "../../store/authStore";

interface UserProfileUpdateReturn {
  updatedProfile: UserProfileUpdateResponseData | null;
  isLoading: boolean;
  error: string | null;
  updateProfile: (profileData: UserProfileUpdateFullRequest) => Promise<boolean>;
  clearError: () => void;
}

export const useUserProfileUpdate = (): UserProfileUpdateReturn => {
  const [updatedProfile, setUpdatedProfile] = useState<UserProfileUpdateResponseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateTokens, updateUserData } = useAuthStore();

  const updateProfile = useCallback(async (profileData: UserProfileUpdateFullRequest): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<UserProfileUpdateResponse>(
        "/api/v1/user/profile/update",
        profileData
      );

      if (response.data.status && response.data.data) {
        setUpdatedProfile(response.data.data);
        
        if (response.data.access_token) {
          updateTokens({ 
            accessToken: response.data.access_token
          });
        }

        updateUserData({
          nickname: response.data.data.nickname,
          gender: response.data.data.gender,
          birth: response.data.data.birth,
        });
        
        return true;
      } else {
        setError("프로필 업데이트에 실패했습니다.");
        return false;
      }
    } catch (error: any) {
      console.error("프로필 업데이트 실패:", error);
      
      const errorMessage = error.response?.data?.message || "프로필 업데이트에 실패했습니다.";
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [updateTokens, updateUserData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { 
    updatedProfile, 
    isLoading, 
    error, 
    updateProfile, 
    clearError 
  };
};
